import { useEffect, useRef } from 'react';
import {Platform } from 'react-native';
import { useUser } from '../../../context/UserContext';
import { useCheckoutContext } from '../../../context/CheckoutContext';
import { BACKEND_URL } from '../../../Config/Const';

// Función para cargar el SDK de MODO en la página web (evita duplicados)
const loadModoSDK = () => {
  if (typeof document === 'undefined') return;
  
  // Detectar si estamos en localhost (test) o en producción
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  const scriptSrc = (typeof window !== 'undefined' && window.location?.port === '8443')
    ? 'https://ecommerce-modal.preprod.modo.com.ar/bundle.js'  // Test/Preprod
    : 'https://ecommerce-modal.modo.com.ar/bundle.js';          // Producción

  const existing = document.querySelector(
    'script[src*="ecommerce-modal.preprod.modo.com.ar/bundle.js"], script[src*="ecommerce-modal.modo.com.ar/bundle.js"]'
  );
  if (existing) return;
  const script = document.createElement('script');
  script.src = scriptSrc;
  script.async = true;
  document.body.appendChild(script);
};
const PagoModo = ({ cupon, total, onClose }) => {

  const { mail, codUsu, cel, codPostal } = useUser();
  const {
    // envío / retiro
    tipoEntrega,
    codDic,
    // facturación
    deseaFactura,
    tipoResponsable,
    razonSocial,
    cuit,
    telefonoFacturacion,
    // horarios
    fechaRetiroSeleccionada,
    codigoHorarioSeleccionado,
    // unificar pedidos
    codCompraUnifica,
    // persona autorizada y observaciones
    nombreAutorizado,
    dniAutorizado,
    observaciones,
    // reemplazo
    aceptaReemplazo,
    // pago
    marcaTarjeta,
    cuotas,
    nroEmision,
    nroPlan,
    // MODO
    datosModo,
    setDatosModo,
  } = useCheckoutContext();

  // Evitar abrir el modal más de una vez
  const openedRef = useRef(false);
  // Evitar generar QR duplicado en la primera carga
  const initialQRGenerated = useRef(false);

  // Esperar a que el SDK esté disponible
  const waitForModoSDK = (timeoutMs = 6000, intervalMs = 100) => new Promise((resolve) => {
    const start = Date.now();
    const iv = setInterval(() => {
      if (typeof window !== 'undefined' && window.ModoSDK) {
        clearInterval(iv);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(iv);
        resolve(false);
      }
    }, intervalMs);
  });


  const generarQR = async () => {
    try {
      let userMail = mail;
      let userCodUsu = codUsu;
      let suc = null;
      
      // Obtener datos de autenticación y sucursal desde localStorage
      if (Platform.OS === 'web') {
        userMail = localStorage.getItem('mail') || mail;
        userCodUsu = localStorage.getItem('codUsu') || codUsu;
        suc = localStorage.getItem('sucursalId');
      }

      const datos = {
        aceptaReemplazo: Boolean(aceptaReemplazo),
        bono: false, // ajustar si existe estado específico
        cel: cel || "",
        teo: telefonoFacturacion || "",
        codCompraUnificar: Number(codCompraUnifica || 0),
        codHorario: codigoHorarioSeleccionado ? Number(codigoHorarioSeleccionado) : 0,
        codPostalEntrega: codPostal ? Number(codPostal) : 2300,
        codPromo: cupon ? cupon : "", // mapear si hay promo
        coddic: codDic ? Number(codDic) : 0,
        cuit: cuit || 0,
        cuotas: cuotas ? Number(cuotas) : 1,
        deseaFactura: Boolean(deseaFactura),
        docAutoriza: dniAutorizado ? Number(String(dniAutorizado).replace(/\D/g, '')) : 0,
        entrega: tipoEntrega ? Number(tipoEntrega) : 1, // 1 retiro, 2 envío
        fencdc: fechaRetiroSeleccionada || "",
        marcaTarjeta: marcaTarjeta || "",
        nombreAutorizado: nombreAutorizado || "",
        nroEmisor: nroEmision ? Number(nroEmision) : null,
        nroPlan: nroPlan ? Number(nroPlan) : null,
        observaciones: observaciones || "",
        rzo: razonSocial || "",
        tipoPago: 5,
        tipoResponsable: tipoResponsable || 0,
        };
      
      const url = `${BACKEND_URL}/rest/fin/pago?mail=${userMail}&codUsu=${userCodUsu}&suc=${suc}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ datos }) // <- el backend recibirá { "datos": { ... } }

      });

      const responseData = await response.json();
      
      // Adaptar al nuevo contrato de /pago
      const result = {
        checkoutId: responseData?.response?.dsResultado?.dsResultado?.modo?.[0]?.id,
        qrString: responseData?.response?.dsResultado?.dsResultado?.modo?.[0]?.qr,
        deeplink: responseData?.response?.dsResultado?.dsResultado?.modo?.[0]?.deeplink,
      };

      if (!result.checkoutId || !result.qrString) {
        console.warn('[PagoModo] Respuesta inválida de /pago', responseData);
        return null;
      }

      // Mantener sincronizado el contexto local
      setDatosModo({ id: result.checkoutId, qr: result.qrString, deepLink: result.deeplink });

      return result;
    } catch (err) {
      return null;
    }
  };


  // Iniciar pago con MODO SDK según la documentación oficial
  const iniciarPagoModo = async (initialData) => {
    try {
      // Permitir usar datos recién obtenidos sin depender del setState asincrónico
      const qrString = initialData?.qrString ?? datosModo?.qr;
      const checkoutId = initialData?.checkoutId ?? datosModo?.id;
      const deepLinkUrl = typeof initialData?.deeplink === 'string'
        ? initialData.deeplink
        : (initialData?.deeplink?.url ?? datosModo?.deepLink);

      const baseUrl = (typeof window !== 'undefined' && window.location)
        ? `${window.location.protocol}//${window.location.host}`
        : '';

      // Bloquear scroll/clicks de la página mientras está abierto el modal
      try {
        if (typeof document !== 'undefined') {
          document.body.style.overflow = 'hidden';
          const root = document.getElementById('root') || document.getElementById('app');
          if (root) root.style.pointerEvents = 'none';
        }
      } catch (e) {}

      const modalObject = {
        qrString,
        checkoutId,
        deeplink: {
          url: deepLinkUrl,
          callbackURL: `${baseUrl}/PagoFail`, // URL de retorno en caso de fallo
          callbackURLSuccess: `${baseUrl}/PagoOK` // URL de retorno en caso de éxito
        },
        callbackURL: `${baseUrl}/PagoOK`, // URL de retorno para flujo Desktop
        // El SDK espera que refreshData devuelva { checkoutId, qrString, deeplink }
        // Solo regenerar si ya se había generado inicialmente (evita duplicados en primera carga)
        refreshData: async () => {
          if (!initialQRGenerated.current) {
            // Primera vez, retornar los datos iniciales sin hacer nueva llamada
            return initialData;
          }
          // Solo regenerar si es un refresh real (QR expirado, etc.)
          return await generarQR();
        },
        onClose: function () {
          // Limpieza para devolver control a la página
          try {
            if (typeof document !== 'undefined') {
              document.body.style.overflow = '';
              if (document.documentElement) document.documentElement.style.overflow = '';
              document.body.classList.remove('modal-open');
              const root = document.getElementById('root') || document.getElementById('app');
              if (root) root.style.pointerEvents = '';
              // Intento de remover overlays comunes del SDK si quedaran presentes
              const candidates = Array.from(document.body.children);
              candidates.forEach((el) => {
                const cls = (el.className || '').toString();
                const id = el.id || '';
                const looksLikeOverlay = (
                  cls.toLowerCase().includes('modo') || id.toLowerCase().includes('modo') || el.getAttribute('data-modo-modal') !== null
                ) && (getComputedStyle(el).position === 'fixed' || getComputedStyle(el).position === 'absolute');
                if (looksLikeOverlay && typeof el.remove === 'function') {
                  el.remove();
                }
              });
              // Eliminar overlays fullscreen genéricos que bloqueen clics
              const fullscreens = document.querySelectorAll('*');
              fullscreens.forEach((el) => {
                try {
                  const cs = getComputedStyle(el);
                  if (cs && cs.position === 'fixed' && cs.zIndex && parseInt(cs.zIndex, 10) >= 1000 && (el === document.body ? false : true)) {
                    if ((el.id || '').toLowerCase().includes('modo') || (el.className || '').toString().toLowerCase().includes('modo')) {
                      if (typeof el.remove === 'function') el.remove();
                    }
                  }
                } catch (_) {}
              });
            }
          } catch (e) {}
          openedRef.current = false;
          if (typeof onClose === 'function') onClose();
          
        }
      };

      // Inicializar el pago con MODO según la documentación oficial
      window.ModoSDK.modoInitPayment(modalObject);
    } catch (err) {
    }
  };

  // Auto abrir: cargar SDK, generar QR inicial y abrir el modal una sola vez
  useEffect(() => {
    let mounted = true;
    const bootstrap = async () => {
      if (Platform.OS === 'web' && !window.ModoSDK) {
        loadModoSDK();
        await waitForModoSDK();
      }
      
      let initial;
      
      // Verificar si ya existen datos de MODO en el contexto (generados por ResumenPedido)
      if (datosModo && datosModo.id && datosModo.qr) {
        // Usar los datos existentes sin hacer nueva llamada a la API
        initial = {
          checkoutId: datosModo.id,
          qrString: datosModo.qr,
          deeplink: datosModo.deepLink
        };
      } else {
        // Solo generar QR si no hay datos previos
        initial = await generarQR();
      }
      
      if (!mounted || !initial) return;
      // Marcar que ya se generó el QR inicial
      initialQRGenerated.current = true;
      if (!openedRef.current) {
        openedRef.current = true;
        iniciarPagoModo(initial);
      }
    };
    bootstrap();
    return () => {
      mounted = false;
      // Cleanup al desmontar para evitar overlays colgando
      try {
        if (typeof document !== 'undefined') {
          document.body.style.overflow = '';
          const root = document.getElementById('root') || document.getElementById('app');
          if (root) root.style.pointerEvents = '';
        }
      } catch (e) {}
      openedRef.current = false;
      initialQRGenerated.current = false;
    };
  }, []);
  return null;
};

export default PagoModo;
